import React, { useState, useEffect, useRef } from 'react';
import { Message, Category } from '../../types';
import apiService from '../../services/api';
import socketService from '../../services/socket';
import soundService from '../../services/soundService';
import FileUpload from '../common/FileUpload';
import CountrySelect from '../common/CountrySelect';
import CompanySuggestion from '../common/CompanySuggestion';
import { useCustomerTranslation } from '../../contexts/CustomerTranslationContext';
import MessageContentRenderer from '../common/MessageContentRenderer';
import { useAuth } from '../../contexts/AuthContext';


interface CustomerInfo {
  name: string;
  email: string;
  company: string;
  phone: string;
  country: string;
  language: string;
}

const CustomerChat: React.FC = () => {
  const { language, setLanguage, t, formatCountdown } = useCustomerTranslation();
  const { user } = useAuth();

  // Helper functions for localStorage
  const saveCustomerInfo = (info: CustomerInfo) => {
    try {
      localStorage.setItem('neurochat_customer_info', JSON.stringify(info));
    } catch (error) {
      console.warn('Failed to save customer info to localStorage:', error);
    }
  };

  // Chat session persistence helpers
  interface ChatSession {
    ticketId: string | null;
    ticketNumber: string | null;
    messages: Message[];
    ticketClosed: boolean;
    closedByTimeout: boolean;
    closedByAgent: boolean;
    surveyCompleted: boolean;
    lastActivity: string;
    sessionId: string;
  }

  const saveChatSession = (sessionData: Partial<ChatSession>) => {
    try {
      const existingSession = loadChatSession();
      const updatedSession = {
        ...existingSession,
        ...sessionData,
        lastActivity: new Date().toISOString()
      };
      // Use sessionStorage for chat sessions - cleared when browser/tab closes
      sessionStorage.setItem('neurochat_session', JSON.stringify(updatedSession));
      console.log('💾 Chat session saved to sessionStorage:', updatedSession.ticketId);
    } catch (error) {
      console.warn('Failed to save chat session to sessionStorage:', error);
    }
  };

  const loadChatSession = (): ChatSession => {
    try {
      // Try sessionStorage first (current session)
      let saved = sessionStorage.getItem('neurochat_session');
      
      // If no session storage, check if we have a localStorage session (migration case)
      if (!saved) {
        const oldSession = localStorage.getItem('neurochat_session');
        if (oldSession) {
          console.log('🔄 Migrating chat session from localStorage to sessionStorage');
          sessionStorage.setItem('neurochat_session', oldSession);
          localStorage.removeItem('neurochat_session');
          saved = oldSession;
        }
      }
      
      if (saved) {
        const session = JSON.parse(saved);
        // Validate session structure
        if (session && typeof session === 'object') {
          return {
            ticketId: session.ticketId || null,
            ticketNumber: session.ticketNumber || null,
            messages: Array.isArray(session.messages) ? session.messages : [],
            ticketClosed: Boolean(session.ticketClosed),
            closedByTimeout: Boolean(session.closedByTimeout),
            closedByAgent: Boolean(session.closedByAgent),
            surveyCompleted: Boolean(session.surveyCompleted),
            lastActivity: session.lastActivity || new Date().toISOString(),
            sessionId: session.sessionId || generateSessionId()
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load chat session from sessionStorage:', error);
    }
    return {
      ticketId: null,
      ticketNumber: null,
      messages: [],
      ticketClosed: false,
      closedByTimeout: false,
      closedByAgent: false,
      surveyCompleted: false,
      lastActivity: new Date().toISOString(),
      sessionId: generateSessionId()
    };
  };

  const clearChatSession = () => {
    try {
      sessionStorage.removeItem('neurochat_session');
      // Also clear any old localStorage session
      localStorage.removeItem('neurochat_session');
      console.log('🗑️ Chat session cleared from both storages');
    } catch (error) {
      console.warn('Failed to clear chat session:', error);
    }
  };

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const loadSavedCustomerInfo = (): CustomerInfo => {
    try {
      const saved = localStorage.getItem('neurochat_customer_info');
      if (saved) {
        const parsedInfo = JSON.parse(saved);
        // Validate the structure
        if (parsedInfo && typeof parsedInfo === 'object' && 
            typeof parsedInfo.name === 'string' &&
            typeof parsedInfo.email === 'string' &&
            typeof parsedInfo.company === 'string' &&
            typeof parsedInfo.phone === 'string') {
          return {
            ...parsedInfo,
            country: parsedInfo.country || '',
            language: parsedInfo.language || 'en'
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load customer info from localStorage:', error);
    }
    return { name: '', email: '', company: '', phone: '', country: '', language: 'en' };
  };

  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>(loadSavedCustomerInfo());
  const [emailError, setEmailError] = useState('');

  // Initialize state from saved session
  const savedSession = loadChatSession();
  const [sessionId] = useState(savedSession.sessionId);
  const [sessionRestored, setSessionRestored] = useState(false);

  // Initialize all state variables first (before useEffect hooks)
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentTicketId, setCurrentTicketId] = useState<string | null>(savedSession.ticketId);
  const [currentTicketNumber, setCurrentTicketNumber] = useState<string | null>(savedSession.ticketNumber);
  const [messages, setMessages] = useState<Message[]>(savedSession.messages);
  const [newMessage, setNewMessage] = useState('');
  const [isInfoComplete, setIsInfoComplete] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const [ticketClosed, setTicketClosed] = useState(savedSession.ticketClosed);
  const [closedByTimeout, setClosedByTimeout] = useState(savedSession.closedByTimeout);
  const [closedByAgent, setClosedByAgent] = useState(savedSession.closedByAgent);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showResolutionFeedback, setShowResolutionFeedback] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showPersistentSurvey, setShowPersistentSurvey] = useState(false);
  const [surveyCompleted, setSurveyCompleted] = useState(savedSession.surveyCompleted);
  const [companySuggestions, setCompanySuggestions] = useState<Array<{name: string; confidence: number; description?: string}>>([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [companyInputTimeout, setCompanyInputTimeout] = useState<NodeJS.Timeout | null>(null);
  const [systemSettings, setSystemSettings] = useState<any>(null);
  // Mobile-specific states
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showAttachmentPreview, setShowAttachmentPreview] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<{url: string, type: string, name: string} | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abandonmentWarningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abandonmentCountdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<Date>(new Date());
  const currentTicketIdRef = useRef<string | null>(null);
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const cameraCanvasRef = useRef<HTMLCanvasElement>(null);
  
  // New state for abandonment warning
  const [showAbandonmentWarning, setShowAbandonmentWarning] = useState(false);
  const [abandonmentCountdown, setAbandonmentCountdown] = useState(30);
  
  // Customer status tracking (for reference only)
  const [customerStatus, setCustomerStatus] = useState<{ isOnline: boolean; lastSeen: string | null }>({ isOnline: true, lastSeen: null });
  
  // Agent assignment tracking
  const [assignedAgent, setAssignedAgent] = useState<{ id: string; name: string; avatar?: string } | null>(null);

  // Initialize language from saved customer info
  useEffect(() => {
    const savedInfo = loadSavedCustomerInfo();
    if (savedInfo.language && savedInfo.language !== language) {
      setLanguage(savedInfo.language);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update language preference when it changes
  useEffect(() => {
    if (customerInfo.language !== language) {
      setCustomerInfo(prev => ({ ...prev, language }));
    }
  }, [language]);

  // Prefill customer information when user is logged in
  useEffect(() => {
    if (user && user.userType === 'customer') {
      console.log('🔄 Prefilling customer info from logged-in user:', user);
      
      // Only prefill if the current customer info is empty or different from logged-in user
      const shouldPrefill = !customerInfo.email || 
                           customerInfo.email !== user.email || 
                           !customerInfo.name || 
                           customerInfo.name !== user.name;
      
      if (shouldPrefill) {
        // First, prefill with basic user info immediately
        const basicPrefilledInfo: CustomerInfo = {
          name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          email: user.email || '',
          company: customerInfo.company || '', // Keep existing company if already filled
          phone: customerInfo.phone || '', // Keep existing phone if already filled
          country: customerInfo.country || '', // Keep existing country if already filled
          language: customerInfo.language || language
        };
        
        console.log('✅ Prefilling basic customer info:', basicPrefilledInfo);
        setCustomerInfo(basicPrefilledInfo);
        
        // Clear any existing email errors since we have a valid logged-in email
        if (emailError && user.email) {
          setEmailError('');
        }
        
        // Try to fetch additional customer profile data
        const fetchCustomerProfile = async () => {
          try {
            console.log('🔍 Fetching additional customer profile data...');
            const customerProfile = await apiService.getCustomer(user.id);
            
            if (customerProfile) {
              const enhancedPrefilledInfo: CustomerInfo = {
                name: customerProfile.name || basicPrefilledInfo.name,
                email: customerProfile.email || basicPrefilledInfo.email,
                company: customerProfile.company || basicPrefilledInfo.company,
                phone: customerProfile.phone || basicPrefilledInfo.phone,
                country: customerProfile.country || basicPrefilledInfo.country,
                language: basicPrefilledInfo.language
              };
              
              console.log('🎉 Enhanced customer info with profile data:', enhancedPrefilledInfo);
              setCustomerInfo(enhancedPrefilledInfo);
            }
          } catch (error) {
            console.warn('⚠️ Could not fetch additional customer profile data:', error);
            // Continue with basic prefilled info if profile fetch fails
          }
        };
        
        fetchCustomerProfile();
      }
    }
  }, [user, customerInfo.email, customerInfo.name, language, emailError]);

  // Save customer info whenever it changes
  useEffect(() => {
    saveCustomerInfo(customerInfo);
  }, [customerInfo]);

  // Persist chat session whenever key state changes
  useEffect(() => {
    if (currentTicketId || messages.length > 0) {
      saveChatSession({
        ticketId: currentTicketId,
        ticketNumber: currentTicketNumber,
        messages,
        ticketClosed,
        closedByTimeout,
        closedByAgent,
        surveyCompleted,
        sessionId
      });
    }
  }, [currentTicketId, currentTicketNumber, messages, ticketClosed, closedByTimeout, closedByAgent, surveyCompleted, sessionId]);

  // Restore chat session on component mount if there's an active session
  useEffect(() => {
    const session = loadChatSession();
    if (session.ticketId && !ticketClosed) {
      console.log('🔄 Restoring chat session:', session.ticketId);
      setSessionRestored(true);
      
      // Hide the restoration indicator after a few seconds
      setTimeout(() => setSessionRestored(false), 3000);
      
      // If we have a ticket ID but no messages, fetch them from the server
      if (session.ticketId && session.messages.length === 0) {
        const fetchMessages = async () => {
          try {
            setLoadingMessages(true);
            const fetchedMessages = await apiService.getMessages(session.ticketId!);
            setMessages(fetchedMessages);
            saveChatSession({ messages: fetchedMessages });
          } catch (error) {
            console.error('Failed to fetch messages for restored session:', error);
          } finally {
            setLoadingMessages(false);
          }
        };
        fetchMessages();
      }

      // Reconnect to socket for this ticket
      if (session.ticketId) {
        socketService.joinTicket(session.ticketId);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle page unload - save session for refresh, but prepare for cleanup on actual close
  useEffect(() => {
    let isRefresh = false;
    
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Check if this is likely a refresh (F5, Ctrl+R) vs actual close
      // We'll save the session and let sessionStorage handle the cleanup on actual browser close
      if (currentTicketId && !ticketClosed) {
        // Always save to sessionStorage - it will be cleared automatically on browser/tab close
        saveChatSession({
          ticketId: currentTicketId,
          ticketNumber: currentTicketNumber,
          messages,
          ticketClosed,
          closedByTimeout,
          closedByAgent,
          surveyCompleted,
          sessionId
        });
        
        console.log('💾 Session saved for potential refresh - will auto-clear on browser close');
      }
    };

    // Handle visibility change (tab switching, minimizing)
    const handleVisibilityChange = () => {
      if (document.hidden && currentTicketId && !ticketClosed) {
        // Save session when tab becomes hidden
        saveChatSession({
          ticketId: currentTicketId,
          ticketNumber: currentTicketNumber,
          messages,
          ticketClosed,
          closedByTimeout,
          closedByAgent,
          surveyCompleted,
          sessionId
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentTicketId, currentTicketNumber, messages, ticketClosed, closedByTimeout, closedByAgent, surveyCompleted, sessionId]);

  // Clear saved customer info
  const clearSavedCustomerInfo = () => {
    try {
      localStorage.removeItem('neurochat_customer_info');
      setCustomerInfo({ name: '', email: '', company: '', phone: '', country: '', language: 'en' });
      setEmailError('');
      setLanguage('en'); // Reset to English
      
      // Also clear chat session when clearing customer info
      clearChatSession();
      
      // Reset chat-related state
      setCurrentTicketId(null);
      setCurrentTicketNumber(null);
      setMessages([]);
      setTicketClosed(false);
      setClosedByTimeout(false);
      setClosedByAgent(false);
      setSurveyCompleted(false);
    } catch (error) {
      console.warn('Failed to clear saved customer info:', error);
    }
  };
  
  // Update ref whenever currentTicketId changes
  useEffect(() => {
    currentTicketIdRef.current = currentTicketId;
  }, [currentTicketId]);

  // Simple connection monitoring (for status display only)
  useEffect(() => {
    if (!currentTicketId || ticketClosed) return;

    const checkConnection = () => {
      const isConnected = socketService.isSocketConnected();
      setCustomerStatus(prev => ({ 
        ...prev, 
        isOnline: isConnected, 
        lastSeen: isConnected ? new Date().toISOString() : prev.lastSeen 
      }));
    };

    // Check connection every 5 seconds
    const connectionCheckInterval = setInterval(checkConnection, 5000);
    
    // Initial check
    checkConnection();

    return () => {
      clearInterval(connectionCheckInterval);
    };
  }, [currentTicketId, ticketClosed]);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
        || window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load system settings
  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/system/settings`);
        if (response.ok) {
          const data = await response.json();
          console.log('🔧 Loaded system settings for chat timeout:', data.settings?.chatAbandonmentTimeout);
          setSystemSettings(data.settings);
        } else {
          console.error('Failed to load system settings, using defaults');
          setSystemSettings({
            chatAbandonmentTimeout: 15, // Default 15 minutes
          });
        }
      } catch (error) {
        console.error('Error loading system settings:', error);
        // Use default values
        setSystemSettings({
          chatAbandonmentTimeout: 15, // Default 15 minutes
        });
      }
    };
    loadSystemSettings();
  }, []);

  useEffect(() => {
    // Only clear authentication tokens if no user is currently authenticated
    // This prevents clearing agent tokens when they accidentally access customer chat
    if (!user) {
      // Clear any existing authentication tokens for anonymous customer chat
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      // Also clear the cached token in ApiService
      apiService.logout();
    }
    
    // Validate loaded email on component mount
    if (customerInfo.email && !validateEmail(customerInfo.email)) {
      setEmailError('Please enter a valid email address');
    }
    
    loadCategories();
    // Connect to Socket.IO without authentication (for anonymous users)
    socketService.connect();

    // Handle browser/tab close to ensure proper disconnect
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      console.log('🚪 beforeunload triggered!', {
        currentTicketId: currentTicketIdRef.current,
        ticketClosed: ticketClosed,
        shouldSendBeacon: currentTicketIdRef.current && !ticketClosed
      });
      
      if (currentTicketIdRef.current && !ticketClosed) {
        // Add abandonment message before closing
        try {
          console.log('📡 Sending browser close abandonment via sendBeacon...');
          // Use sendBeacon for reliable notification on page unload (REVERTED TO ORIGINAL)
          const result = navigator.sendBeacon(
            `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/tickets/${currentTicketIdRef.current}/abandon`,
            JSON.stringify({ reason: 'browser_close' })
          );
          console.log('📡 sendBeacon result:', result);
          
          socketService.leaveTicket(currentTicketIdRef.current);
          // Small delay to allow the message to be sent
          const start = Date.now();
          while (Date.now() - start < 100) {
            // Blocking delay to ensure message is sent
          }
        } catch (error) {
          console.error('Error leaving ticket on page unload:', error);
        }
      } else {
        console.log('🚫 Skipping abandonment beacon - no active ticket or already closed');
      }
      socketService.forceDisconnect();
    };

    // Handle visibility change (tab switch/minimize) - disconnect after delay
    let visibilityTimeoutId: NodeJS.Timeout | null = null;
    
    const handleVisibilityChange = () => {
      if (document.hidden && currentTicketIdRef.current && !ticketClosed) {
        console.log('👁️ Tab hidden - setting abandonment timer for ticket:', currentTicketIdRef.current);
        const timeoutMs = (systemSettings?.chatAbandonmentTimeout || 15) * 60 * 1000;
        console.log(`⏰ Will abandon in ${timeoutMs/1000/60} minutes if tab stays hidden`);
        
        // User switched away from tab - set a timer to mark as abandoned after some delay
        visibilityTimeoutId = setTimeout(() => {
          if (document.hidden && currentTicketIdRef.current) {
            console.log('🔴 Customer tab hidden for too long, marking ticket as abandoned');
            console.log('📡 Making tab-hidden abandonment API call');
            
            // Mark ticket as abandoned
            try {
              fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/tickets/${currentTicketIdRef.current}/abandon`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reason: 'tab_hidden' })
              }).then(response => {
                console.log('📡 Tab-hidden abandonment API response:', response.status);
                if (!response.ok) {
                  console.error('❌ Tab-hidden abandonment API failed:', response.status);
                }
                return response.json();
              }).then(result => {
                console.log('✅ Tab-hidden abandonment API successful:', result);
              }).catch(error => {
                console.error('❌ Tab-hidden abandonment API error:', error);
              });
            } catch (error) {
              console.error('Error marking ticket as abandoned:', error);
            }
            socketService.leaveTicket(currentTicketIdRef.current);
          }
        }, timeoutMs);
      } else if (!document.hidden && visibilityTimeoutId) {
        console.log('👁️ Tab visible again - canceling abandonment timer');
        // User came back to tab - cancel the abandon timer
        clearTimeout(visibilityTimeoutId);
        visibilityTimeoutId = null;
        
        // Rejoin the ticket if we have one
        if (currentTicketIdRef.current && !socketService.isSocketConnected()) {
          console.log('🟢 Customer tab visible again, reconnecting...');
          socketService.connect();
          setTimeout(() => {
            if (currentTicketIdRef.current) {
              socketService.joinTicket(currentTicketIdRef.current, true);
            }
          }, 1000);
        }
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    


    return () => {
      // Clear visibility timeout
      if (visibilityTimeoutId) {
        clearTimeout(visibilityTimeoutId);
      }
      
      // Clear company input timeout
      if (companyInputTimeout) {
        clearTimeout(companyInputTimeout);
      }
      
      // Remove event listeners
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Leave ticket room before disconnecting if we have a current ticket
      if (currentTicketIdRef.current) {
        socketService.leaveTicket(currentTicketIdRef.current);
      }
      socketService.disconnect();
    };
  }, [user]);

  useEffect(() => {
    const infoComplete = customerInfo.name.trim() && customerInfo.email.trim() && customerInfo.country.trim();
    setIsInfoComplete(!!infoComplete);
  }, [customerInfo]);

  useEffect(() => {
    // Only auto-scroll when explicitly enabled (for new messages, not initial loads)
    if (shouldAutoScroll && !loadingMessages) {
      scrollToBottom();
      setShouldAutoScroll(false); // Reset the flag after scrolling
    }
  }, [messages, shouldAutoScroll, loadingMessages]);

  useEffect(() => {
    if (currentTicketId) {
      // Join the ticket room for real-time updates (as customer)
      socketService.joinTicket(currentTicketId, true);
      
      // Listen for new messages
      const handleNewMessage = (data: { message: Message }) => {
        console.log('📨 Received new message via Socket.IO:', data.message);
        console.log('📨 Message sender structure:', (data.message as any).sender);
        console.log('📨 Message user structure:', data.message.user);
        console.log('📨 Message userId:', data.message.userId);
        console.log('📨 Message senderId:', data.message.senderId);
        console.log('📨 Full message structure:', JSON.stringify(data.message, null, 2));
        
        // Check if this is an AI message and update assigned agent
        const sender = (data.message as any).sender;
        if (sender?.userType === 'ai' && currentTicketId && data.message.ticketId === currentTicketId) {
          // Set AI agent as assigned
          setAssignedAgent({
            id: sender.id || 'ai',
            name: `${sender.firstName || 'NeuroAI'} ${sender.lastName || 'Assistant'}`.trim()
          });
        }
        
        // Play sound notification for agent messages
        if (data.message.sender?.userType === 'agent') {
          console.log('🔊 Playing sound for agent message');
          soundService.playTicketUpdateSound();
          // Reset inactivity timer when agent responds
          resetInactivityTimer();
        }
        
        setMessages(prev => {
          // Avoid duplicates
          if (prev.find(m => m.id === data.message.id)) {
            return prev;
          }
          setShouldAutoScroll(true); // Auto-scroll for new incoming messages
          return [...prev, data.message];
        });
      };

      // Listen for typing indicators
      const handleTyping = (data: { ticketId: string; isTyping: boolean }) => {
        console.log('⌨️ Typing indicator:', data);
        setIsTyping(data.isTyping);
      };

      // Listen for AI ticket details updates
      const handleTicketDetailsUpdated = (data: {
        ticketId: string;
        title: string;
        description: string;
        confidence: number;
        generatedAt: string;
      }) => {
        if (currentTicketId && data.ticketId === currentTicketId) {
          console.log('🤖 Customer received AI ticket details update:', data);
          // For customers, we'll show a subtle notification that AI has analyzed the conversation
          // The title/description update is mainly for agents, but we can still log it
        }
      };



      // Listen for customer status changes (to detect when we go offline)
      const handleCustomerStatusChanged = (data: {
        ticketId: string;
        isOnline: boolean;
        lastSeen: string;
      }) => {
        if (currentTicketId && data.ticketId === currentTicketId && !data.isOnline) {
          console.log('🔴 Customer went offline');
          // Email mode is now handled on the agent side
          
          // Add email mode notification message
          const emailModeMessage: Message = {
            id: `email-offline-${Date.now()}`,
            ticketId: currentTicketId,
            content: 'You appear to be offline. Chat has been converted to email mode - your messages will be sent via email.',
            createdAt: new Date().toISOString(),
            messageType: 'system' as const,
            sender: {
              id: null,
              firstName: 'System',
              lastName: '',
              userType: 'system' as const
            }
          };
          
          setMessages(prev => {
            setShouldAutoScroll(true); // Auto-scroll for email mode message
            return [...prev, emailModeMessage];
          });
        }
      };

      // Listen for ticket claimed events (when agent takes over)
      const handleTicketClaimed = (data: {
        ticketId: string;
        agent: {
          id: string;
          firstName: string;
          lastName: string;
        };
        claimedAt: string;
        handoffFromAI?: boolean;
        autoClaimed?: boolean;
        autoAssignedViaMessage?: boolean;
      }) => {
        if (currentTicketId && data.ticketId === currentTicketId) {
          console.log('🎯 Customer received ticket claimed event:', data);
          
          const agentName = `${data.agent.firstName} ${data.agent.lastName}`.trim();
          setAssignedAgent({
            id: data.agent.id,
            name: agentName
          });

          // Add notification message
          const claimMessage: Message = {
            id: `claim-${Date.now()}`,
            ticketId: currentTicketId,
            content: data.handoffFromAI 
              ? `${agentName} has taken over your chat from our AI assistant and will continue helping you.`
              : `${agentName} has joined your chat and will be assisting you.`,
            createdAt: new Date().toISOString(),
            messageType: 'system' as const,
            sender: {
              id: null,
              firstName: 'System',
              lastName: '',
              userType: 'system' as const
            }
          };
          
          setMessages(prev => {
            setShouldAutoScroll(true); // Auto-scroll for claim message
            return [...prev, claimMessage];
          });
        }
      };

      // Listen for AI to human handoff events
      const handleAIToHumanHandoff = (data: {
        ticketId: string;
        previousAgent: string;
        newAgent: {
          id: string;
          firstName: string;
          lastName: string;
        };
        handoffAt: string;
      }) => {
        if (currentTicketId && data.ticketId === currentTicketId) {
          console.log('🤖➡️👨 Customer received AI handoff event:', data);
          
          const agentName = `${data.newAgent.firstName} ${data.newAgent.lastName}`.trim();
          setAssignedAgent({
            id: data.newAgent.id,
            name: agentName
          });
        }
      };

      // Listen for ticket updates (including when agent closes ticket)
      const handleTicketUpdate = (data: any) => {
        if (currentTicketId && data.ticketId === currentTicketId) {
          console.log('🎫 Customer received ticket update:', data);
          
          // Check if agent closed the ticket
          if (data.updates.status === 'closed-by-agent-resolved') {
            console.log('🚫 Ticket closed by agent, closing customer interface');
            setTicketClosed(true);
            setClosedByAgent(true);
            setClosedByTimeout(false);
            
            // Add closure message to chat
            const agentClosureMessage = {
              id: `agent-closure-${Date.now()}`,
              ticketId: currentTicketId,
              content: 'Agent closed the ticket and marked as resolved. If you still need help, please reopen the ticket or call technical support line.',
              createdAt: new Date().toISOString(),
              messageType: 'system' as const,
              sender: {
                id: null,
                firstName: 'System',
                lastName: '',
                userType: 'system' as const
              }
            };
            setMessages(prev => {
              setShouldAutoScroll(true); // Auto-scroll for agent closure message
              return [...prev, agentClosureMessage];
            });
            
            // Clear inactivity timers
            if (inactivityTimeoutRef.current) {
              clearTimeout(inactivityTimeoutRef.current);
              inactivityTimeoutRef.current = null;
            }
            if (abandonmentWarningTimeoutRef.current) {
              clearTimeout(abandonmentWarningTimeoutRef.current);
              abandonmentWarningTimeoutRef.current = null;
            }
            if (abandonmentCountdownIntervalRef.current) {
              clearInterval(abandonmentCountdownIntervalRef.current);
              abandonmentCountdownIntervalRef.current = null;
            }
            
            // Leave the ticket room
            socketService.leaveTicket(currentTicketId);
            
            // Open satisfaction survey when agent closes ticket
            console.log('📋 Opening satisfaction survey after agent closed ticket');
            setTimeout(() => {
              openSatisfactionSurvey();
            }, 1000); // Small delay to ensure the close process completes and message is shown
          }
        }
      };

      socketService.on('new_message', handleNewMessage);
      socketService.on('user_typing', handleTyping);
      socketService.on('ticket_details_updated', handleTicketDetailsUpdated);
      socketService.on('ticket_updated', handleTicketUpdate);
      socketService.on('ticket_claimed', handleTicketClaimed);
      socketService.on('ai_to_human_handoff', handleAIToHumanHandoff);

      socketService.on('customer_status_changed', handleCustomerStatusChanged);

      return () => {
        socketService.off('new_message', handleNewMessage);
        socketService.off('user_typing', handleTyping);
        socketService.off('ticket_details_updated', handleTicketDetailsUpdated);
        socketService.off('ticket_updated', handleTicketUpdate);
        socketService.off('ticket_claimed', handleTicketClaimed);
        socketService.off('ai_to_human_handoff', handleAIToHumanHandoff);

        socketService.off('customer_status_changed', handleCustomerStatusChanged);
        // Leave ticket room when changing tickets
        if (currentTicketId) {
          socketService.leaveTicket(currentTicketId);
        }
      };
    }
  }, [currentTicketId]);

  // Start inactivity timer when ticket is created
  useEffect(() => {
    if (currentTicketId && !ticketClosed) {
      resetInactivityTimer();
    }
    
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, [currentTicketId, ticketClosed]);

  const loadCategories = async () => {
    try {
      // Use the dropdown options API to get categories configured in system settings
      const dropdownOptions = await apiService.getDropdownOptions();
      const activeCategories = dropdownOptions.categories.filter(cat => (cat as any).isActive !== false);
      setCategories(activeCategories);
      // Don't auto-select the first category - let users choose
    } catch (error) {
      console.error('Error loading categories from dropdown options:', error);
      // Fallback to original method if dropdown options fail
      try {
        const fetchedCategories = await apiService.getCategories();
        setCategories(fetchedCategories);
        // Don't auto-select the first category - let users choose
      } catch (fallbackError) {
        console.error('Error loading categories (fallback):', fallbackError);
      }
    }
  };

  const loadMessages = async (ticketId: string) => {
    try {
      setLoadingMessages(true);
      console.log('📥 Loading messages for ticket:', ticketId);
      const fetchedMessages = await apiService.getMessages(ticketId);
      console.log('📥 Loaded messages:', fetchedMessages);
      setMessages(fetchedMessages || []);
    } catch (error) {
      console.error('❌ Error loading messages:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInfoChange = (field: keyof CustomerInfo, value: string) => {
    const updatedInfo = { ...customerInfo, [field]: value };
    setCustomerInfo(updatedInfo);
    
    // Save to localStorage immediately
    saveCustomerInfo(updatedInfo);
    
    // Real-time email validation
    if (field === 'email') {
      if (value && !validateEmail(value)) {
        setEmailError(t.invalidEmail);
      } else {
        setEmailError('');
      }
    }

        // Handle company field changes for real-time suggestions
    if (field === 'company') {
      // Clear existing timeout
      if (companyInputTimeout) {
        clearTimeout(companyInputTimeout);
      }

      if (value.trim().length >= 2) {
        // Set a debounced timeout to fetch suggestions
        const timeout = setTimeout(async () => {
          try {
            const response = await apiService.suggestCompanyRealtime(value.trim(), 25);
            if (response.suggestions.length > 0) {
              setCompanySuggestions(response.suggestions);
              setShowCompanySuggestions(true);
            } else {
              setShowCompanySuggestions(false);
            }
          } catch (error) {
            console.error('Error fetching company suggestions:', error);
            setShowCompanySuggestions(false);
          }
        }, 500); // 500ms delay

        setCompanyInputTimeout(timeout);
      } else {
        setShowCompanySuggestions(false);
      }
    }
    
    // Check if all required info is complete
    const isComplete = updatedInfo.name.trim() !== '' && 
                      updatedInfo.email.trim() !== '' && 
                      validateEmail(updatedInfo.email) &&
                      updatedInfo.country.trim() !== '' &&
                      emailError === '';
    setIsInfoComplete(isComplete);
  };

  const handleCompanySelect = (companyName: string) => {
    const updatedInfo = { ...customerInfo, company: companyName };
    setCustomerInfo(updatedInfo);
    saveCustomerInfo(updatedInfo);
    setShowCompanySuggestions(false);
    
    // Check if all required info is complete
    const isComplete = updatedInfo.name.trim() !== '' && 
                      updatedInfo.email.trim() !== '' && 
                      validateEmail(updatedInfo.email) &&
                      updatedInfo.country.trim() !== '' &&
                      emailError === '';
    setIsInfoComplete(isComplete);
  };

  const handleDismissCompanySuggestions = () => {
    setShowCompanySuggestions(false);
  };

  const createTicketFromFirstMessage = async (firstMessage: string) => {
    try {
      console.log('🎫 Creating ticket with customer info:', customerInfo);
      console.log('🎫 First message:', firstMessage);
      
      // Use the first available category or create a default one
      const defaultCategoryId = categories.length > 0 ? categories[0].id : '1';
      
      const ticketData = {
        title: `Support Request from ${customerInfo.name}`,
        description: firstMessage,
        priority: 'medium' as const,
        categoryId: defaultCategoryId
      };

      const customerData = {
        name: customerInfo.name,
        email: customerInfo.email,
        company: customerInfo.company,
        phone: customerInfo.phone,
        country: customerInfo.country
      };

      console.log('🎫 Ticket data:', ticketData);
      console.log('🎫 Customer data:', customerData);

      const ticket = await apiService.createTicket(ticketData, customerData);
      console.log('🎫 Ticket created successfully:', ticket);
      
      setCurrentTicketId(ticket.id);
      setCurrentTicketNumber(ticket.ticketNumber);
      
      // Show persistent survey after chat initiation
      console.log('🔧 DEBUG: Showing persistent survey, current ticket ID:', ticket.id);
      setShowPersistentSurvey(true);
      
      // Load messages for the newly created ticket
      await loadMessages(ticket.id);
      
      return true;
    } catch (error) {
      console.error('❌ Error creating ticket:', error);
      console.error('❌ Error details:', error instanceof Error ? error.message : String(error));
      console.error('❌ Full error:', error);
      return false;
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || ticketClosed) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    
    // Reset inactivity timer on message send
    resetInactivityTimer();

    if (!currentTicketId) {
      // This is the first message, create a ticket
      setLoading(true);
      const success = await createTicketFromFirstMessage(messageContent);
      setLoading(false);
      
      if (!success) {
        setNewMessage(messageContent); // Restore message on error
        alert(t.failedToStart);
      }
    } else {
      // Send message to existing ticket
      try {
        console.log('📤 Sending message to ticket:', currentTicketId);
        
        // Send via regular chat API (will also broadcast via Socket.IO)
        const sentMessage = await apiService.sendMessage(currentTicketId, messageContent);
        console.log('📤 Message sent successfully:', sentMessage);
        
        // Add message to local state (if not already added by Socket.IO)
        setMessages(prev => {
          if (prev.find(m => m.id === sentMessage.id)) {
            return prev;
          }
          setShouldAutoScroll(true); // Auto-scroll for sent message
          return [...prev, sentMessage];
        });
        
      } catch (error) {
        console.error('❌ Error sending message:', error);
        setNewMessage(messageContent); // Restore message on error
        alert(t.failedToSend);
      }
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleFileSelect = async (files: FileList) => {
    if (!currentTicketId || ticketClosed) {
      console.log('❌ Cannot upload: no ticket ID or ticket closed', { currentTicketId, ticketClosed });
      return;
    }

    console.log('📎 Starting file upload for ticket:', currentTicketId);
    const fileArray = Array.from(files);
    setUploadingFiles(fileArray);

    try {
      for (const file of fileArray) {
        console.log('📎 Uploading file:', file.name, 'size:', file.size, 'type:', file.type);
        
        const result = await apiService.uploadFile(currentTicketId, file);
        console.log('📎 File uploaded successfully:', result);

        // Add the file message to local state (if not already added by Socket.IO)
        setMessages(prev => {
          if (prev.find(m => m.id === result.message.id)) {
            return prev;
          }
          setShouldAutoScroll(true); // Auto-scroll for file upload message
          return [...prev, result.message];
        });
      }
    } catch (error) {
      console.error('❌ Error uploading files:', error);
      alert(`${t.failedToUpload}: ${error instanceof Error ? error.message : String(error)}. ${t.failedToStart.split('.')[1] || ''}`);
    } finally {
      setUploadingFiles([]);
      setShowFileUpload(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return `0 ${t.bytes}`;
    const k = 1024;
    const sizes = [t.bytes, t.kb, t.mb, t.gb];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string, messageType: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (messageType === 'image') {
      return (
        <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    
    switch (extension) {
      case 'pdf':
        return (
          <svg className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'doc':
      case 'docx':
        return (
          <svg className="h-4 w-4 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'xls':
      case 'xlsx':
        return (
          <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0V17m0-10a2 2 0 012-2h2a2 2 0 002-2" />
          </svg>
        );
      case 'zip':
      case 'rar':
      case '7z':
        return (
          <svg className="h-4 w-4 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
        );
      default:
        return (
          <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  // Camera functions for mobile
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera by default
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        cameraVideoRef.current.play();
      }
      setShowCameraCapture(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert(t.cameraPermissionError);
    }
  };

  const stopCamera = () => {
    if (cameraVideoRef.current && cameraVideoRef.current.srcObject) {
      const stream = cameraVideoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      cameraVideoRef.current.srcObject = null;
    }
    setShowCameraCapture(false);
  };

  const capturePhoto = () => {
    if (cameraVideoRef.current && cameraCanvasRef.current) {
      const video = cameraVideoRef.current;
      const canvas = cameraCanvasRef.current;
      const context = canvas.getContext('2d');
      
      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0);
        
        canvas.toBlob(async (blob) => {
          if (blob) {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const file = new File([blob], `photo-${timestamp}.jpg`, { type: 'image/jpeg' });
            const fileList = Object.assign(Object.create(FileList.prototype), {
              0: file,
              length: 1,
              item: (index: number) => index === 0 ? file : null
            }) as FileList;
            
            await handleFileSelect(fileList);
            stopCamera();
          }
        }, 'image/jpeg', 0.9);
      }
    }
  };

  // Enhanced attachment preview for mobile
  const showAttachmentInPreview = (url: string, type: string, name: string) => {
    setPreviewAttachment({ url, type, name });
    setShowAttachmentPreview(true);
  };

  const closeAttachmentPreview = () => {
    setShowAttachmentPreview(false);
    setPreviewAttachment(null);
  };

  // Reset inactivity timer
  const resetInactivityTimer = () => {
    lastActivityRef.current = new Date();
    
    console.log('⏰ Resetting inactivity timer for ticket:', currentTicketId);
    console.log('📊 Current state:', { 
      ticketClosed, 
      hasSystemSettings: !!systemSettings,
      abandonmentTimeout: systemSettings?.chatAbandonmentTimeout 
    });
    
    // Clear existing timers
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      console.log('🔄 Cleared existing inactivity timer');
    }
    if (abandonmentWarningTimeoutRef.current) {
      clearTimeout(abandonmentWarningTimeoutRef.current);
      console.log('🔄 Cleared existing warning timer');
    }
    if (abandonmentCountdownIntervalRef.current) {
      clearInterval(abandonmentCountdownIntervalRef.current);
      abandonmentCountdownIntervalRef.current = null;
      console.log('🔄 Cleared existing countdown interval');
    }
    
    // Hide warning if it's showing
    if (showAbandonmentWarning) {
      setShowAbandonmentWarning(false);
    }
    
    if (currentTicketId && !ticketClosed && systemSettings) {
      const abandonmentTimeoutMs = (systemSettings?.chatAbandonmentTimeout || 15) * 60 * 1000;
      const warningDelayMs = abandonmentTimeoutMs - 30000; // 30 seconds before timeout
      
      console.log(`⏱️ Setting up timers:
        - Total timeout: ${abandonmentTimeoutMs}ms (${abandonmentTimeoutMs/1000/60} minutes)
        - Warning delay: ${warningDelayMs}ms
        - Will warn at: ${new Date(Date.now() + warningDelayMs).toLocaleTimeString()}
        - Will abandon at: ${new Date(Date.now() + abandonmentTimeoutMs).toLocaleTimeString()}`);
      
      // Set warning timer (shows 30 seconds before abandonment)
      if (warningDelayMs > 0) {
        abandonmentWarningTimeoutRef.current = setTimeout(() => {
          console.log('⚠️ Warning timer triggered - showing abandonment warning dialog');
          showAbandonmentWarningDialog();
        }, warningDelayMs);
        
        console.log('✅ Warning timer set successfully - abandonment will be handled by warning dialog');
      } else {
        // If warning delay is too short, trigger abandonment immediately
        console.log('⚠️ Warning delay too short, triggering immediate abandonment');
        inactivityTimeoutRef.current = setTimeout(() => {
          console.log('💀 Direct abandonment timer triggered (no warning)');
          handleAbandonmentTimeout();
        }, abandonmentTimeoutMs);
        
        console.log('✅ Direct abandonment timer set successfully');
      }
      
      console.log('✅ Timers set successfully');
    } else {
      console.log('❌ Cannot set timers:', {
        hasTicketId: !!currentTicketId,
        isNotClosed: !ticketClosed,
        hasSettings: !!systemSettings
      });
    }
  };

  // Show abandonment warning dialog
  const showAbandonmentWarningDialog = () => {
    if (ticketClosed || !currentTicketId) {
      console.log('🚫 Cannot show abandonment warning - ticket closed or no ID');
      return;
    }
    
    // Clear any existing countdown interval
    if (abandonmentCountdownIntervalRef.current) {
      clearInterval(abandonmentCountdownIntervalRef.current);
      abandonmentCountdownIntervalRef.current = null;
      console.log('🔄 Cleared existing countdown interval');
    }
    
    console.log('⚠️ Showing abandonment warning dialog - 30 second countdown started');
    setShowAbandonmentWarning(true);
    setAbandonmentCountdown(30);
    
    // Start countdown and store reference
    abandonmentCountdownIntervalRef.current = setInterval(() => {
      setAbandonmentCountdown(prev => {
        const newValue = prev - 1;
        console.log(`⏰ Abandonment countdown: ${newValue} seconds remaining`);
        
        if (newValue <= 0) {
          // Clear the interval immediately
          if (abandonmentCountdownIntervalRef.current) {
            clearInterval(abandonmentCountdownIntervalRef.current);
            abandonmentCountdownIntervalRef.current = null;
          }
          
          console.log('💀 Countdown reached zero - triggering abandonment now');
          console.log('🔍 Pre-abandonment state check:', {
            currentTicketId,
            ticketClosed,
            hasCurrentTicketId: !!currentTicketId,
            isTicketClosed: !!ticketClosed
          });
          
          // Hide the warning dialog
          setShowAbandonmentWarning(false);
          
          // Trigger abandonment immediately when countdown reaches zero
          console.log('🚀 About to call handleAbandonmentTimeout...');
          setTimeout(() => {
            console.log('🔥 Executing handleAbandonmentTimeout now');
            handleAbandonmentTimeout().catch(error => {
              console.error('❌ Error in handleAbandonmentTimeout:', error);
            });
          }, 100); // Small delay to ensure state updates
          
          return 0;
        }
        return newValue;
      });
    }, 1000);
    
    console.log('✅ Countdown interval started and stored in ref');
  };

  // Handle customer confirming they're still present
  const handleConfirmPresence = () => {
    console.log('🟢 Customer confirmed presence - canceling abandonment and resetting timer');
    
    // Hide the warning dialog
    setShowAbandonmentWarning(false);
    
    // Clear any running timers
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
      console.log('🔄 Cleared inactivity timer due to customer confirmation');
    }
    if (abandonmentWarningTimeoutRef.current) {
      clearTimeout(abandonmentWarningTimeoutRef.current);
      abandonmentWarningTimeoutRef.current = null;
      console.log('🔄 Cleared warning timer due to customer confirmation');
    }
    if (abandonmentCountdownIntervalRef.current) {
      clearInterval(abandonmentCountdownIntervalRef.current);
      abandonmentCountdownIntervalRef.current = null;
      console.log('🔄 Cleared countdown interval due to customer confirmation');
    }
    
    // Reset the timer with fresh activity
    resetInactivityTimer();
    
    console.log('✅ Customer presence confirmed and timers reset');
  };

  // Handle closing the abandonment warning (close chat button)
  const handleCloseAbandonmentWarning = () => {
    console.log('🔴 Customer chose to close chat from abandonment warning');
    
    // Clear the countdown interval
    if (abandonmentCountdownIntervalRef.current) {
      clearInterval(abandonmentCountdownIntervalRef.current);
      abandonmentCountdownIntervalRef.current = null;
      console.log('🔄 Cleared countdown interval due to close chat action');
    }
    
    // Hide the warning dialog
    setShowAbandonmentWarning(false);
    
    // Trigger abandonment immediately
    console.log('🚀 Triggering immediate abandonment due to close chat action');
    setTimeout(() => {
      handleAbandonmentTimeout().catch(error => {
        console.error('❌ Error in handleAbandonmentTimeout from close chat:', error);
      });
    }, 100); // Small delay to ensure state updates
  };

  // Helper function to open customer satisfaction survey
  const openSatisfactionSurvey = () => {
    console.log('📋 Opening customer satisfaction survey in new tab');
    try {
      window.open('https://survey.alchemer.com/s3/8140941/Customer-Service-Satisfaction-2025B', '_blank');
      console.log('✅ Survey opened successfully');
    } catch (error) {
      console.error('❌ Error opening survey:', error);
    }
  };

  // Handle abandonment timeout (called when customer doesn't respond to warning)
  const handleAbandonmentTimeout = async () => {
    console.log('🎯 handleAbandonmentTimeout called!');
    console.log('🔍 Current state:', {
      currentTicketId,
      ticketClosed,
      hasCurrentTicketId: !!currentTicketId,
      isTicketClosed: !!ticketClosed,
      currentTicketIdType: typeof currentTicketId,
      ticketClosedType: typeof ticketClosed
    });
    
    if (!currentTicketId || ticketClosed) {
      console.log('🚫 Abandonment skipped - no ticket ID or already closed', { 
        currentTicketId, 
        ticketClosed,
        reason: !currentTicketId ? 'No ticket ID' : 'Ticket already closed'
      });
      return;
    }
    
    console.log('🚪 ABANDONMENT TRIGGERED - Customer timeout for ticket:', currentTicketId);
    console.log('🔍 Timeout reason: inactivity_timeout');
    
    try {
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/tickets/${currentTicketId}/abandon`;
      console.log('📡 Making abandonment API call to:', apiUrl);
      
      // Mark ticket as abandoned
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'inactivity_timeout' })
      });
      
      console.log('📡 Abandonment API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Abandonment API failed:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Abandonment API successful:', result);
      
      console.log('🔄 Setting ticket state to closed...');
      setTicketClosed(true);
      setClosedByTimeout(true);
      setShowAbandonmentWarning(false);
      console.log('✅ Ticket state updated to closed');
      
      // Add abandonment message to chat body
      const abandonmentMessage = {
        id: `abandonment-${Date.now()}`,
        ticketId: currentTicketId,
        content: t.conversationClosedDueToInactivity,
        createdAt: new Date().toISOString(),
        messageType: 'system' as const,
        sender: {
          id: null,
          firstName: t.systemMessage,
          lastName: '',
          userType: 'system' as const
        }
      };
      setMessages(prev => {
        setShouldAutoScroll(true); // Auto-scroll for abandonment message
        return [...prev, abandonmentMessage];
      });
      
      // Leave the ticket room
      console.log('🚪 Leaving ticket room:', currentTicketId);
      socketService.leaveTicket(currentTicketId);
      
      // Clear timers
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      if (abandonmentWarningTimeoutRef.current) {
        clearTimeout(abandonmentWarningTimeoutRef.current);
        abandonmentWarningTimeoutRef.current = null;
      }
      if (abandonmentCountdownIntervalRef.current) {
        clearInterval(abandonmentCountdownIntervalRef.current);
        abandonmentCountdownIntervalRef.current = null;
      }
      
      // Clear chat session on successful abandonment
      console.log('🗑️ Clearing chat session due to successful abandonment');
      clearChatSession();
      
      // Open satisfaction survey after timeout
      console.log('📋 Opening satisfaction survey due to session timeout');
      setTimeout(() => {
        openSatisfactionSurvey();
      }, 1000); // Small delay to ensure the chat state is updated
      
      console.log('🏁 Abandonment process completed successfully');
    } catch (error) {
      console.error('❌ Error marking ticket as abandoned:', error);
      console.error('📋 Full error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });
      
      // Even if API fails, close the chat locally to provide user feedback
      console.log('🔄 API failed, but closing chat locally anyway');
      setTicketClosed(true);
      setClosedByTimeout(true);
      setShowAbandonmentWarning(false);
      
      // Add local abandonment message
      const abandonmentMessage = {
        id: `abandonment-local-${Date.now()}`,
        ticketId: currentTicketId,
        content: 'Chat session ended (connection issues may have prevented proper closure)',
        createdAt: new Date().toISOString(),
        messageType: 'system' as const,
        sender: {
          id: null,
          firstName: t.systemMessage,
          lastName: '',
          userType: 'system' as const
        }
      };
      setMessages(prev => {
        setShouldAutoScroll(true); // Auto-scroll for abandonment message
        return [...prev, abandonmentMessage];
      });
      
      // Still clean up socket connection
      console.log('🚪 Leaving ticket room despite API error');
      socketService.leaveTicket(currentTicketId);
      
      // Clear timers
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      if (abandonmentWarningTimeoutRef.current) {
        clearTimeout(abandonmentWarningTimeoutRef.current);
        abandonmentWarningTimeoutRef.current = null;
      }
      if (abandonmentCountdownIntervalRef.current) {
        clearInterval(abandonmentCountdownIntervalRef.current);
        abandonmentCountdownIntervalRef.current = null;
      }
      
      // Clear chat session on abandonment
      console.log('🗑️ Clearing chat session due to abandonment');
      clearChatSession();
      
      console.log('🏁 Local abandonment process completed despite API error');
    }
  };

  // Handle timeout close (kept for backward compatibility)
  const handleTimeoutClose = async () => {
    await handleAbandonmentTimeout();
  };

  // Manual test abandonment (for testing purposes)
  const handleTestAbandonment = async () => {
    console.log('🧪 handleTestAbandonment called');
    console.log('🧪 Test state check:', {
      currentTicketId,
      ticketClosed,
      hasCurrentTicketId: !!currentTicketId,
      isTicketClosed: !!ticketClosed
    });
    
    if (!currentTicketId || ticketClosed) {
      console.log('🚫 Cannot test abandonment - no ticket or already closed');
      return;
    }
    
    console.log('🧪 MANUAL TEST ABANDONMENT TRIGGERED - calling handleAbandonmentTimeout');
    try {
      await handleAbandonmentTimeout();
      console.log('🧪 Manual test abandonment completed');
    } catch (error) {
      console.error('🧪 Manual test abandonment failed:', error);
    }
  };

  // Add keyboard shortcuts for testing
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'A') {
        event.preventDefault();
        console.log('🧪 Test abandonment keyboard shortcut pressed');
        handleTestAbandonment();
      }
      // Quick test with 10 second timeout (Ctrl+Shift+T)
      if (event.ctrlKey && event.shiftKey && event.key === 'T') {
        event.preventDefault();
        console.log('🧪 Quick test: Setting 10-second timeout for testing');
        
        // Clear existing timers
        if (inactivityTimeoutRef.current) {
          clearTimeout(inactivityTimeoutRef.current);
        }
        if (abandonmentWarningTimeoutRef.current) {
          clearTimeout(abandonmentWarningTimeoutRef.current);
        }
        if (abandonmentCountdownIntervalRef.current) {
          clearInterval(abandonmentCountdownIntervalRef.current);
          abandonmentCountdownIntervalRef.current = null;
        }
        
        // Set quick 10-second test (5 seconds warning + 5 seconds countdown)
        if (currentTicketId && !ticketClosed) {
          console.log('⏱️ Quick test: Warning in 5 seconds, abandonment in 10 seconds');
          abandonmentWarningTimeoutRef.current = setTimeout(() => {
            console.log('⚠️ Quick test: Showing abandonment warning dialog');
            showAbandonmentWarningDialog();
          }, 5000); // 5 seconds
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentTicketId, ticketClosed]);

  // Handle initial close ticket confirmation
  const handleInitialCloseTicket = () => {
    setShowCloseConfirmation(false);
    // Only show resolution feedback popup if survey hasn't been completed
    if (!surveyCompleted) {
      setShowResolutionFeedback(true);
    } else {
      // Survey already completed, close directly
      handleCloseTicket();
    }
  };

  // Handle persistent survey feedback (without closing ticket)
  const handlePersistentSurveyFeedback = async (resolution: 'resolved' | 'not_resolved' | 'partially_resolved') => {
    if (!currentTicketId) {
      console.error('❌ No current ticket ID available for feedback');
      alert('No active ticket found. Please try again.');
      return;
    }

    try {
      console.log('🔧 DEBUG: handlePersistentSurveyFeedback called with:', { resolution, currentTicketId });
      
      // Send feedback to backend without closing ticket
      await apiService.submitTicketFeedback(currentTicketId, resolution);
      console.log('🔧 DEBUG: Feedback submitted successfully');
      
      // Mark survey as completed
      setSurveyCompleted(true);
      
      // Show success message
      console.log('✅ Thank you for your feedback!');
      
    } catch (error) {
      console.error('❌ Error submitting feedback:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        currentTicketId,
        resolution
      });
      alert(`${t.failedToSubmitFeedback}: ${error instanceof Error ? error.message : String(error)}. ${t.failedToStart.split('.')[1] || ''}`);
    }
  };

    // Handle resolution feedback and close ticket
  const handleResolutionFeedback = async (resolution: 'resolved' | 'not_resolved' | 'partially_resolved') => {
    console.log('🔧 DEBUG: handleResolutionFeedback called with:', { resolution, currentTicketId, ticketClosed });
    
    if (!currentTicketId || ticketClosed) {
      console.error('🔧 DEBUG: Cannot proceed - no ticket ID or ticket already closed:', { currentTicketId, ticketClosed });
      return;
    }
    
    try {
      console.log('🔧 DEBUG: About to call closeTicketWithFeedback with:', { currentTicketId, resolution });
      await apiService.closeTicketWithFeedback(currentTicketId, resolution);
      console.log('🔧 DEBUG: closeTicketWithFeedback succeeded');
      
      setTicketClosed(true);
      setClosedByTimeout(false);
      setShowResolutionFeedback(false);
      
      // Mark survey as completed since feedback was provided via modal
      setSurveyCompleted(true);
      
      // Leave the ticket room
      if (currentTicketId) {
        socketService.leaveTicket(currentTicketId);
      }
      
      // Clear the chat session since user explicitly closed the ticket
      clearChatSession();
      
      // Clear the timeout
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      
      // Open satisfaction survey after feedback close
      console.log('📋 Opening satisfaction survey after feedback submission');
      setTimeout(() => {
        openSatisfactionSurvey();
      }, 500); // Small delay to ensure the close process completes
    } catch (error) {
      console.error('❌ Error closing ticket with feedback:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        currentTicketId,
        resolution
      });
      alert(t.failedToClose);
    }
  };

  // Handle manual close ticket (legacy method for timeout)
  const handleCloseTicket = async () => {
    if (!currentTicketId || ticketClosed) return;
    
    try {
      await apiService.closeTicket(currentTicketId);
      setTicketClosed(true);
      setClosedByTimeout(false);
      setShowCloseConfirmation(false);
      
      // Leave the ticket room
      if (currentTicketId) {
        socketService.leaveTicket(currentTicketId);
      }
      
      // Clear the chat session since user explicitly closed the ticket
      clearChatSession();
      
      // Clear the timeout
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      
      // Open satisfaction survey after manual close
      console.log('📋 Opening satisfaction survey due to manual chat close');
      setTimeout(() => {
        openSatisfactionSurvey();
      }, 500); // Small delay to ensure the close process completes
    } catch (error) {
      console.error('❌ Error closing ticket:', error);
      alert(t.failedToClose);
    }
  };

  // Start new conversation
  const handleStartNewConversation = () => {
    setCurrentTicketId(null);
    setCurrentTicketNumber(null);
    setMessages([]);
    setTicketClosed(false);
    setClosedByTimeout(false);
    setClosedByAgent(false);
    setNewMessage('');
    
    // Reset survey state
    setShowPersistentSurvey(false);
    setSurveyCompleted(false);
    
    // Clear timeout
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  };

  // Cleanup function
  useEffect(() => {
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (abandonmentWarningTimeoutRef.current) {
        clearTimeout(abandonmentWarningTimeoutRef.current);
      }
      if (abandonmentCountdownIntervalRef.current) {
        clearInterval(abandonmentCountdownIntervalRef.current);
      }
    };
  }, []);



  return (
    <div className={`max-w-full mx-auto px-3 sm:px-4 ${isMobile ? 'py-4' : 'py-4'}`}>
        <div className={`bg-white dark:bg-gray-800 ${isMobile ? 'rounded-lg shadow-sm' : 'rounded-lg shadow-sm'} border border-gray-200 dark:border-gray-700`}>
          {/* Customer Info Form */}
          {!currentTicketId && (
            <div className={`${isMobile ? 'p-4' : 'p-6'} border-b border-gray-200 dark:border-gray-700`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div className="flex items-center justify-between sm:justify-start flex-1">
                  <h2 className={`font-medium text-gray-900 dark:text-gray-100 ${isMobile ? 'text-base' : 'text-lg'}`}>
                    {t.formTitle}
                  </h2>
                  {user && user.userType === 'customer' && (
                    <div className="flex items-center text-xs text-green-600 dark:text-green-400 ml-3">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Prefilled from account
                    </div>
                  )}
                </div>
                {/* Language Selection - Compact inline selector */}
                {!currentTicketId && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap flex items-center">
                      <svg className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                      </svg>
                      {t.languageLabel}
                    </label>
                    <select
                      value={language}
                      onChange={(e) => {
                        const newLanguage = e.target.value;
                        setLanguage(newLanguage);
                        setCustomerInfo(prev => ({ ...prev, language: newLanguage }));
                      }}
                      className={`px-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isMobile ? 'py-2 w-32' : 'py-1.5 w-36'}`}
                    >
                      <option value="en">🇬🇧 English</option>
                      <option value="es">🇪🇸 Español</option>
                      <option value="pt">🇵🇹 Português</option>
                      <option value="de">🇩🇪 Deutsch</option>
                      <option value="fr">🇫🇷 Français</option>
                    </select>
                  </div>
                )}
              </div>

              {/* First row: Name, Email, Phone */}
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.nameLabel}
                    {user && user.userType === 'customer' && customerInfo.name && (
                      <span className="ml-1 text-xs text-green-600 dark:text-green-400">(from account)</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={customerInfo.name}
                    onChange={(e) => handleInfoChange('name', e.target.value)}
                    placeholder={t.namePlaceholder}
                    readOnly={!!(user && user.userType === 'customer' && customerInfo.name)}
                    className={`w-full px-3 border rounded-md text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isMobile ? 'py-3 text-base' : 'py-2'} ${
                      user && user.userType === 'customer' && customerInfo.name
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.emailLabel}
                    {user && user.userType === 'customer' && customerInfo.email && (
                      <span className="ml-1 text-xs text-green-600 dark:text-green-400">(from account)</span>
                    )}
                  </label>
                  <input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => handleInfoChange('email', e.target.value)}
                    placeholder={t.emailPlaceholder}
                    readOnly={!!(user && user.userType === 'customer' && customerInfo.email)}
                    className={`w-full px-3 border rounded-md text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isMobile ? 'py-3 text-base' : 'py-2'} ${
                      emailError 
                        ? 'border-red-500 dark:border-red-500 bg-white dark:bg-gray-700' 
                        : user && user.userType === 'customer' && customerInfo.email
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {emailError && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{emailError}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.phoneLabel}
                  </label>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => handleInfoChange('phone', e.target.value)}
                    placeholder={t.phonePlaceholder}
                    className={`w-full px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isMobile ? 'py-3 text-base' : 'py-2'}`}
                  />
                </div>
              </div>
              
              {/* Second row: Company and Country */}
              <div className={`grid gap-4 mt-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.companyLabel}
                  </label>
                  <input
                    type="text"
                    value={customerInfo.company}
                    onChange={(e) => handleInfoChange('company', e.target.value)}
                    placeholder={t.companyPlaceholder}
                    className={`w-full px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isMobile ? 'py-3 text-base' : 'py-2'}`}
                  />
                  {showCompanySuggestions && (
                    <CompanySuggestion
                      suggestions={companySuggestions}
                      onSelect={handleCompanySelect}
                      onDismiss={handleDismissCompanySuggestions}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.countryLabel}
                  </label>
                  <CountrySelect
                    value={customerInfo.country}
                    onChange={(value) => handleInfoChange('country', value)}
                    placeholder={t.countryPlaceholder}
                    className="text-sm"
                    required={true}
                  />
                </div>
              </div>

              {/* Saved Info Indicator and Clear Button */}
              {(customerInfo.name || customerInfo.email || customerInfo.phone || customerInfo.company || customerInfo.country) && (
                <div className="mt-4 flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <div className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-blue-800 dark:text-blue-200">
                      {t.savedInfoMessage}
                    </span>
                  </div>
                  <button
                    onClick={clearSavedCustomerInfo}
                    type="button"
                    className="text-xs font-medium text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 transition-colors"
                  >
                    {t.clearSavedInfo}
                  </button>
                </div>
              )}
            </div>
                      )}

            {/* Email Mode Indicator */}
            {false && (
              <div className={`${isMobile ? 'px-4 py-2' : 'px-6 py-3'} bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800`}>
                <div className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <p className={`font-medium text-blue-800 dark:text-blue-200 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                      📧 Email Mode Active
                    </p>
                    <p className={`text-blue-600 dark:text-blue-400 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                      Email mode is now handled on the agent side
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Chat Area - Optimized for better space usage */}
          <div className="flex flex-col" style={{ height: currentTicketId ? (isMobile ? '70vh' : '700px') : (isMobile ? '50vh' : '500px') }}>
            {/* Ticket Header - Show when ticket is created */}
            {currentTicketId && currentTicketNumber && !ticketClosed && (
              <div className={`${isMobile ? 'px-4 py-2' : 'px-6 py-3'} bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className={`font-medium text-green-800 dark:text-green-200 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        {t.ticketCreated}: #{currentTicketNumber}
                      </p>
                      <p className={`text-green-600 dark:text-green-400 ${isMobile ? 'text-xs hidden' : 'text-xs'}`}>
                        {isMobile ? '' : t.conversationStarted}
                      </p>
                      {assignedAgent && (
                        <div className={`flex items-center space-x-1 mt-1 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                          {assignedAgent.id === 'ai' || assignedAgent.name.includes('NeuroAI') ? (
                            <>
                              <svg className="h-3 w-3 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <span className="text-blue-600 dark:text-blue-400 font-medium">
                                🤖 {assignedAgent.name}
                              </span>
                            </>
                          ) : (
                            <>
                              <svg className="h-3 w-3 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                                👨‍💼 {assignedAgent.name}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">

                    <button
                      onClick={() => setShowCloseConfirmation(true)}
                      className={`font-medium text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-400 dark:bg-red-900/20 dark:hover:bg-red-900/30 rounded-md transition-colors ${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-xs'}`}
                    >
                      {isMobile ? t.close : t.closeTicket}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Ticket Closed Message */}
            {ticketClosed && (
              <div className={`px-6 py-4 border-b ${
                closedByTimeout 
                  ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                  : closedByAgent 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                    : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700'
              }`}>
                {/* Reference Information Bar */}
                {(currentTicketNumber || assignedAgent) && (
                  <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 011 1v1a1 1 0 01-1 1H3a1 1 0 01-1-1V5a1 1 0 011-1h4z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8v12a2 2 0 002 2h10a2 2 0 002-2V8" />
                      </svg>
                      <span className="font-medium text-gray-700 dark:text-gray-200">Reference Information:</span>
                    </div>
                    <div className="mt-2 space-y-1">
                      {currentTicketNumber && (
                        <div className="flex items-center">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20">Ticket #:</span>
                          <span className="text-sm font-mono font-semibold text-gray-800 dark:text-gray-200">{currentTicketNumber}</span>
                        </div>
                      )}
                      {assignedAgent && (
                        <div className="flex items-center">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20">Agent:</span>
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                            {assignedAgent.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <svg className={`h-6 w-6 mt-0.5 ${
                    closedByTimeout 
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : closedByAgent 
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400'
                  }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {closedByAgent ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                  </svg>
                  <div>
                    <p className={`text-sm font-medium ${
                      closedByTimeout 
                        ? 'text-yellow-800 dark:text-yellow-200'
                        : closedByAgent 
                          ? 'text-blue-800 dark:text-blue-200'
                          : 'text-gray-800 dark:text-gray-200'
                    }`}>
                      {closedByTimeout 
                        ? t.conversationClosedInactivity
                        : closedByAgent 
                          ? t.ticketClosedByAgent
                          : t.conversationClosed
                      }
                    </p>
                    <p className={`text-xs mt-1 ${
                      closedByTimeout 
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : closedByAgent 
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {closedByTimeout 
                        ? 'This conversation was automatically closed after inactivity. If you need further assistance, please start a new conversation below.'
                        : closedByAgent 
                          ? 'Agent closed the ticket and marked as resolved. If you still need help, please reopen the ticket or call technical support line.'
                          : 'This conversation has been closed. If you need further assistance, please start a new conversation below.'
                      }
                    </p>
                    <button
                      onClick={handleStartNewConversation}
                      className="mt-3 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
                    >
                      {t.startNewConversation}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Messages */}
            <div className={`flex-1 overflow-y-auto space-y-4 ${isMobile ? 'p-3' : 'p-6'}`} style={{ minHeight: currentTicketId ? '500px' : 'auto' }}>
              {ticketClosed ? (
                <div className="flex justify-center items-center h-full">
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    <svg className={`mx-auto h-16 w-16 mb-4 ${
                      closedByAgent 
                        ? 'text-blue-400 dark:text-blue-500' 
                        : 'text-gray-400 dark:text-gray-500'
                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      {closedByAgent ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      )}
                    </svg>
                    <p className="text-lg font-medium">
                      {closedByTimeout 
                        ? t.sessionTimedOut
                        : closedByAgent 
                          ? t.ticketResolvedByAgent
                          : t.conversationEnded
                      }
                    </p>
                    <p className="text-sm mt-1">
                      {closedByAgent 
                        ? t.thankYouResolved
                        : t.thankYou
                      }
                    </p>
                  </div>
                </div>
              ) : loadingMessages ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : messages.length === 0 && !currentTicketId ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                  <svg className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t.welcomeToSupport}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {t.welcomeToSupportDesc}
                  </p>
                  {isInfoComplete ? (
                    <button
                      onClick={() => createTicketFromFirstMessage(t.initialMessage)}
                      disabled={loading}
                      className={`inline-flex items-center ${isMobile ? 'w-full justify-center px-4 py-4 text-lg' : 'px-6 py-3 text-base'} font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors ${loading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Starting Chat...
                        </>
                      ) : (
                        <>
                          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          {t.startChat}
                        </>
                      )}
                    </button>
                  ) : (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        Please fill in your details above to start chatting with our support team.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Session restoration indicator */}
                  {sessionRestored && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 animate-fade-in">
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          Chat session restored! Your conversation continues from where you left off.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {messages.map((message) => {
                    // Customer-specific message classification
                    const sender = (message as any).sender;
                    const senderUserType = sender?.userType;
                    
                    // In customer chat, determine message type based on sender.userType
                    let isSystemMessage = false;
                    let isCustomerMessage = false;
                    let isAgentMessage = false;
                    
                    // System messages
                    if (senderUserType === 'system' || message.messageType === 'system') {
                      isSystemMessage = true;
                    }
                    // Agent/AI messages (from support)
                    else if (senderUserType === 'agent' || senderUserType === 'ai') {
                      isAgentMessage = true;
                    }
                    // Customer messages (anonymous or identified customer)
                    else if (senderUserType === 'customer' || !sender || (!message.userId && !message.senderId)) {
                      isCustomerMessage = true;
                    }
                    // Default: if we can't determine, assume it's from customer in this context
                    else {
                      isCustomerMessage = true;
                    }
                    
                    // Debug logging for message classification
                    console.log('🔍 Customer Chat Message classification:', {
                      messageId: message.id,
                      senderUserType: senderUserType,
                      isCustomerMessage: isCustomerMessage,
                      isAgentMessage: isAgentMessage,
                      isSystemMessage: isSystemMessage,
                      sender: sender,
                      content: message.content.substring(0, 30) + '...'
                    });
                    
                    // Handle email notification messages differently
                    if (message.messageType === 'email_notification') {
                      return (
                        <div key={message.id} className="flex justify-center my-2">
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2 max-w-md">
                            <div className="flex items-center space-x-2">
                              <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <p className="text-sm text-blue-800 dark:text-blue-200 break-words whitespace-pre-wrap">{message.content}</p>
                            </div>
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 text-center">
                              {formatTime(message.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    
                    // Handle system messages differently
                    if (isSystemMessage) {
                      return (
                        <div key={message.id} className="flex justify-center my-2">
                          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg px-4 py-2 max-w-md">
                            <div className="flex items-center space-x-2">
                              <svg className="h-4 w-4 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p className="text-sm text-yellow-800 dark:text-yellow-200 break-words whitespace-pre-wrap">{message.content}</p>
                            </div>
                            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 text-center">
                              {formatTime(message.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    
                    // Get sender name for agent messages
                    const senderName = sender && sender.firstName && sender.lastName 
                      ? `${sender.firstName} ${sender.lastName}`.trim()
                      : sender?.name || null;
                    
                    return (
                      <div
                        key={message.id}
                        className={`flex ${isCustomerMessage ? 'justify-end' : 'justify-start'} mb-2`}
                      >
                        <div className={`px-4 py-2 rounded-lg ${isMobile ? 'max-w-[85%]' : 'max-w-xs lg:max-w-md'} ${
                          isCustomerMessage
                            ? 'bg-blue-600 text-white rounded-br-sm'
                            : 'bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-100 border border-green-200 dark:border-green-800 rounded-bl-sm'
                        }`}>
                          {/* File message rendering */}
                          {(message.messageType === 'file' || message.messageType === 'image') && message.fileName ? (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                {getFileIcon(message.fileName, message.messageType)}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm truncate font-medium">{message.fileName}</p>
                                  {message.fileSize && (
                                    <p className={`text-xs ${
                                      isCustomerMessage 
                                        ? 'text-blue-100' 
                                        : 'text-green-700 dark:text-green-300'
                                    }`}>
                                      {formatFileSize(message.fileSize)}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Image preview */}
                              {message.messageType === 'image' && message.fileUrl && (
                                <div className="mt-2">
                                  <img 
                                    src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}${message.fileUrl}`}
                                    alt={message.fileName}
                                    className="max-w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => {
                                      if (isMobile) {
                                        showAttachmentInPreview(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}${message.fileUrl}`, message.messageType || 'image', message.fileName || 'Image');
                                      } else {
                                        window.open(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}${message.fileUrl}`, '_blank');
                                      }
                                    }}
                                    onError={(e) => {
                                      console.error('Image load error:', e);
                                      e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23f3f4f6"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%236b7280">Image</text></svg>';
                                    }}
                                  />
                                </div>
                              )}
                              
                              {/* Download button for files */}
                              {message.fileUrl && (
                                <button
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/files/${message.fileUrl?.split('/').pop()}`, '_blank')}
                                  className={`text-xs underline hover:no-underline ${
                                    isCustomerMessage 
                                      ? 'text-blue-100 hover:text-blue-200' 
                                      : 'text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200'
                                  }`}
                                >
                                  {t.download}
                                </button>
                              )}
                            </div>
                          ) : (
                            <MessageContentRenderer 
                              content={message.content} 
                              className="text-sm break-words"
                            />
                          )}
                          
                          <p className={`text-xs mt-1 ${
                            isCustomerMessage 
                              ? 'text-blue-100' 
                              : 'text-green-700 dark:text-green-300'
                          }`}>
                            {(() => {
                              if (isCustomerMessage) return t.you;
                              if (senderUserType === 'ai') return senderName || 'NeuroAI';
                              return senderName || t.supportAgent;
                            })()} • {formatTime(message.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-lg">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input - Mobile Optimized */}
            {!ticketClosed && (
              <div className={`border-t border-gray-200 dark:border-gray-700 ${isMobile ? 'p-3' : 'p-4'}`}>
                {/* File Upload Area */}
                {showFileUpload && (
                  <div className="mb-4">
                    <FileUpload
                      onFileSelect={handleFileSelect}
                      disabled={!isInfoComplete || uploadingFiles.length > 0}
                      multiple={true}
                      className="w-full"
                    />
                    {uploadingFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {uploadingFiles.map((file, index) => (
                          <div key={index} className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
                            <span>{t.uploading} {file.name}...</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div className={`flex ${isMobile ? 'space-x-1' : 'space-x-2'}`}>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                    placeholder={isInfoComplete ? t.typeMessage : t.fillDetailsAbove}
                    disabled={!isInfoComplete || loading || ticketClosed}
                    className={`flex-1 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-800 ${isMobile ? 'py-3 text-base' : 'py-2'}`}
                  />
                  

                  
                  {/* File Upload Button */}
                  <button
                    type="button"
                    onClick={() => setShowFileUpload(!showFileUpload)}
                    disabled={!isInfoComplete || loading || ticketClosed}
                    className={`rounded-md transition-colors ${isMobile ? 'px-2 py-2' : 'px-3 py-2'} ${
                      showFileUpload 
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={t.attachFiles}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </button>
                  
                  <button
                    type="submit"
                    onClick={handleSendMessage}
                    disabled={!isInfoComplete || !newMessage.trim() || loading || ticketClosed}
                    className={`bg-primary-600 hover:bg-primary-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${isMobile ? 'px-3 py-2' : 'px-4 py-2'}`}
                    title='Send Message'
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Persistent Survey Widget - Under Chat Box */}
            {showPersistentSurvey && !ticketClosed && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      {surveyCompleted ? t.surveyTitleCompleted : t.surveyTitle}
                    </span>
                  </div>
                  
                  {surveyCompleted && (
                    <button
                      onClick={() => setShowPersistentSurvey(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {!surveyCompleted && (
                  <div className="space-y-2">
                    <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                      {t.surveyMessage}
                    </p>
                    <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'flex-row space-x-2'} items-stretch`}>
                      <button
                        onClick={() => handlePersistentSurveyFeedback('resolved')}
                        className={`flex items-center justify-center space-x-1 ${isMobile ? 'px-3 py-2' : 'px-2 py-1'} text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors flex-1`}
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{t.surveyResolved}</span>
                      </button>
                      <button
                        onClick={() => handlePersistentSurveyFeedback('partially_resolved')}
                        className={`flex items-center justify-center space-x-1 ${isMobile ? 'px-3 py-2' : 'px-2 py-1'} text-xs font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors flex-1`}
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{t.surveyPartiallyResolved}</span>
                      </button>
                      <button
                        onClick={() => handlePersistentSurveyFeedback('not_resolved')}
                        className={`flex items-center justify-center space-x-1 ${isMobile ? 'px-3 py-2' : 'px-2 py-1'} text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors flex-1`}
                      >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>{t.surveyNotResolved}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Close Confirmation Modal */}
        {showCloseConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.308 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t.closeConversationTitle}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t.closeConversationMessage}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCloseConfirmation(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleInitialCloseTicket}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                {t.closeConversationButton}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolution Feedback Dialog */}
      {showResolutionFeedback && currentTicketId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t.problemResolvedTitle}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t.problemResolvedMessage}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleResolutionFeedback('resolved')}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t.problemResolved}</span>
              </button>
              <button
                onClick={() => handleResolutionFeedback('partially_resolved')}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t.problemPartiallyResolved}</span>
              </button>
              <button
                onClick={() => handleResolutionFeedback('not_resolved')}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors flex items-center justify-center space-x-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>{t.problemNotResolved}</span>
              </button>
              <button
                onClick={() => setShowResolutionFeedback(false)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Abandonment Warning Dialog */}
      {showAbandonmentWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t.stillThereTitle}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {t.stillThereMessage}
              </p>
              <p className="text-xs text-gray-400 mb-6">
                {t.stillThereCountdown.split('{seconds}')[0] || ''}<span className="font-semibold text-red-600">{abandonmentCountdown}</span>{t.stillThereCountdown.split('{seconds}')[1] || ''}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleConfirmPresence}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium"
                >
                  {t.yesImHere}
                </button>
                <button
                  onClick={handleCloseAbandonmentWarning}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  {t.closeChat}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerChat; 